import { useMemo, useState, useEffect } from "react";
import { MIXPANEL_EVENTS } from "constants/mixpanelEvents";
import Mixpanel from "utils/mixpanel";
import Homescreen from "./Homescreen";
import VisaRequirementsScreen from "./VisaRequirementsScreen";
import PassportDetailScreen from "./PassportDetailScreen";
import VisaForm from "./VisaForm";
import FinalSuccessScreen from "./FinalSuccessScreen";
import { TEDCApplicant } from "@smvtech/x-flux";
import DocumentsCompletedScreen from "./DocumentsCompletedScreen";
import { useLocation } from "react-router-dom";
import NoApplicant from "../NoApplicant";

type Props = {
	activeApplicantData: TEDCApplicant | null;
};

type Step =
	| "start"
	| "visaRequirements"
	| "passportDetail"
	| "documentsCompleted"
	| "visaForm"
	| "success"
	| "noApplicant";

function resolveInitialStep(activeApplicantData: TEDCApplicant | null, search: string): Step {
	const params = new URLSearchParams(search);

	const applicationId = params.get("application_id");
	const pid = params.get("pid");

	if (applicationId) return "start";
	if (!activeApplicantData && pid) return "passportDetail";
	return "start";
}

export default function MobileFlow({ activeApplicantData }: Props) {
	const location = useLocation();

	const resolvedStep = useMemo(
		() => resolveInitialStep(activeApplicantData, location.search),
		[activeApplicantData, location.search]
	);

	const [step, setStep] = useState<Step>(resolvedStep);
	const [visaFormInitialTab, setVisaFormInitialTab] = useState<"documents" | "form">("documents");

	useEffect(() => {
		Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.SCREEN_VIEWED, { screen: step });
	}, [step]);

	const goTo = (next: Step) => setStep(next);

	const handleSuccess = () => goTo("success");

	switch (step) {
		case "start":
			return <Homescreen onNext={() => goTo("visaRequirements")} />;

		case "visaRequirements":
			return (
				<VisaRequirementsScreen
					onPrev={() => goTo("start")}
					onSelectDocuments={() => {
						setVisaFormInitialTab("documents");
						goTo("visaForm");
					}}
					onSelectForm={() => {
						setVisaFormInitialTab("form");
						goTo("visaForm");
					}}
				/>
			);

		case "passportDetail":
			return <PassportDetailScreen onNext={() => goTo("visaRequirements")} />;

		case "documentsCompleted":
			return <DocumentsCompletedScreen onNext={() => goTo("visaForm")} />;

		case "visaForm":
			if (!activeApplicantData) {
				return null;
			}
			return (
				<VisaForm
					activeApplicantData={activeApplicantData}
					initialTab={visaFormInitialTab}
					onNext={handleSuccess}
					onPrev={() => goTo("visaRequirements")}
					onDocumentsCompleted={() => {
						setVisaFormInitialTab("form");
						goTo("documentsCompleted");
					}}
				/>
			);

		case "success":
			return (
				<FinalSuccessScreen
					onPrev={() => goTo("visaForm")}
					travellerName={activeApplicantData?.traveller?.first_name}
				/>
			);

		case "noApplicant":
			return <NoApplicant />;

		default:
			return null;
	}
}

